import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {ToastService} from '../../toast/toast.service';
import {finalize, Subject, takeUntil} from 'rxjs';
import {StorageService} from '../../../services/storage-service';
import {RegionService} from '../../../services/region-service';
import {CountryService} from '../../../services/country.service';
import {LeadService} from '../../../services/lead.service';
import {Country} from '../../../models/country';
import {Lead} from '../../../models/lead';
import {AnalyticsService} from '../../../services/analytic.service';

const REFERENCE = "WEB_JOIN_DETAILS"

@Component({
  selector: 'app-join-details',
  imports: [
    ReactiveFormsModule,
    NgSwitch,
    RouterLink,
    FormsModule,
    NgForOf,
    NgIf,
    NgSwitchCase,
    NgSwitchDefault
  ],
  templateUrl: './join-details.component.html',
  styleUrl: './join-details.component.scss'
})
export class JoinDetailsComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  // Region
  regionSlug: string | null = null

  // Lead
  isLeadLoading = false
  isLeadLoaded = false
  leadId: string | null = null
  lead: Lead | null = null

  // Promotion
  promotionCode: string | null = null
  productSlug: string | null = null

  // Countries
  isCountriesLoading = false;
  isCountriesLoaded = false
  countries: Country[] = []

  // Input
  fullName: string | null = null
  email: string | null = null
  countrySlug: string | null = null
  postalCode: string | null = null

  submitted = false;


  constructor(private router: Router,
              private storage: StorageService,
              private toast: ToastService,
              private regionService: RegionService,
              private countryService: CountryService,
              private leadService: LeadService,
              private analyticService: AnalyticsService) {
  }

  async ngOnInit() {
    this.regionSlug = await this.regionService.getRegionSlug();
    this.countrySlug = await this.countryService.getCountrySlug();

    this.leadId = this.storage.get('leadId') || null;
    this.promotionCode = this.storage.get('promotionCode') || null;
    this.productSlug = this.storage.get('productSlug') || null;

    this.refreshLead();
    this.refreshCountries();
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshLead() {
    if (!this.leadId) return;

    this.isLeadLoading = true;
    this.leadService.getById(this.leadId).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isLeadLoading = false
        this.isLeadLoaded = true
      }))
      .subscribe({
        next: (lead) => {
          this.lead = lead;

          this.email = lead.email || null;
          this.fullName = (lead.firstName && lead.lastName) ? (lead.firstName + " " + lead.lastName) : null;
          this.countrySlug = lead.country || null
          this.postalCode = lead.postalCode || null
        },
        error: () => {
          this.storage.remove('leadId');
        }
      });
  }

  refreshCountries() {
    this.isCountriesLoading = true
    this.countryService.findCountries(false, false).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isCountriesLoading = false
        this.isCountriesLoaded = true
      }))
      .subscribe(countries => {
        this.countries = countries;
      });
  }

  submit() {
    if (!this.fullName) return;
    if (!this.email) return;
    if (!this.countrySlug) return;
    if (!this.postalCode) return;

    // Validate full name
    const fullNameTokens = this.fullName.split(' ').filter(token => token.length > 0);
    if (fullNameTokens.length <= 1) {
      this.toast.failure('Your first and last name are required.');
      return;
    }

    const email = this.email
    const reference = this.leadService.generateReference(REFERENCE);
    const firstName = fullNameTokens[0];
    fullNameTokens.shift();
    const lastName = fullNameTokens.join(' ');
    const postalCode = this.postalCode;
    const country = this.countrySlug

    this.submitted = true
    this.leadService.createPostalCodeLead(email, reference, firstName, lastName, postalCode, country).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.submitted = false))
      .subscribe(lead => {
        this.storage.add(StorageService.LEAD_ID, lead._id);

        // Store any changed regions or counties
        if (this.regionSlug) this.regionService.regionSlug$.next(this.regionSlug);
        if (this.countrySlug) this.countryService.countrySlug$.next(this.countrySlug);

        // Update analytic lead
        this.analyticService.leadCustomer();

        this.router.navigate(['/join', 'payment']).finally();
      })
  }

}
